/* eslint-disable no-nested-ternary */

import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Layout, Menu, Button, Icon, Badge, Spin, Modal as AntdModal, Tooltip, Dropdown, message } from 'antd';
import * as HttpStatus from 'http-status-codes';
import classNames from 'classnames';
import Logo from '../Logo/Logo';
import avivaLogo from '../Logo/aviva-logo.svg';
import {
  headerMainLogoSelector,
  headerMenuLogoSelector,
  headerMenuDualBrandingSelector,
  businessInfoSelector,
} from '../../scenes/Organisation/OrganisationSelectors';
import {
  loadingPlanSubscriptionsSelector,
  getExpiryDate,
  planExpiryDateSelector,
  planSubscriptionsSelector,
  planSelector,
  plansSelector,
} from '../../scenes/Dashboard/scenes/Plan/PlanSelectors';
import { renewPlan, fetchPlanSubscriptions, selectPlan } from '../../scenes/Dashboard/scenes/Plan/PlanAction';
import { brokerInfoSelector } from '../../scenes/Admin/scenes/Broker/BrokerSelectors';
import { selectOrganisation } from '../../scenes/Organisation/OrganisationAction';
import PlanEdit, { PLAN_ACTIONS } from '../../scenes/Dashboard/scenes/Plan/components/PlanEdit';
import { MSG_ERROR, MSG_PAYMENT_REQUIRED } from '../../constants';
import onActivateKeyDown from '../../utils/a11y';
import { getApiErrorMessage, parseHttpError } from '../../utils/httpError';
import { getPlanExpiryStatus, PLAN_EXPIRY_STATUS_LABEL } from '../PlanExpiryBadge/plan-expiry-shared';
import Modal from '../Modal/Modal';
import Login from '../Login/Login';
import { secureStorage } from '../../utils/secureStorage';
import HelpTip from '../../scenes/Admin/scenes/Subscription/scenes/BrokerageSubscriptions/components/HelpTip';
import resolveHeaderLocation from './headerLocationTrail';
import './Header.scss';

const AntHeader = Layout.Header;

function renderLogo({ logoType, className }) {
  if (logoType === 'none') return null;
  if (logoType === 'aviva') {
    return (
      <img
        src={avivaLogo}
        className={className}
        alt="Aviva"
        style={{ display: 'block', objectFit: 'contain', maxHeight: '100%', width: 'auto' }}
      />
    );
  }
  return <Logo className={className} height="auto" width="auto" />;
}

function hasSelectablePlan(plan) {
  return Boolean(plan && typeof plan === 'object' && plan.planId != null);
}

const HEADER_COMPACT_AFTER_PX = 48;

const MOBILE_PLAN_STATUS_CLASS_BY_EXPIRY = {
  expired: 'header-mobile-plan-status--danger',
  'expiring-soon': 'header-mobile-plan-status--warning',
  'not-expired': 'header-mobile-plan-status--ok',
};

const PLAN_STATUS_META = {
  'not-expired': { key: 'active', label: 'Active' },
  'expiring-soon': { key: 'expiring', label: 'Expiring' },
  expired: { key: 'inactive', label: 'Inactive' },
  inactive: { key: 'inactive', label: 'Inactive' },
};

function getPlanStatusMeta(expiryStatus) {
  return PLAN_STATUS_META[expiryStatus] || PLAN_STATUS_META.inactive;
}

function PlanStatusBadge({ status, compact = false }) {
  const meta = getPlanStatusMeta(status);
  return (
    <span
      className={classNames(
        'header-plan-status-badge',
        `header-plan-status-badge--${meta.key}`,
        compact && 'header-plan-status-badge--compact',
      )}
    >
      {compact ? <span className="header-plan-status-badge__dot" aria-hidden /> : null}
      <span className="header-plan-status-badge__label">{meta.label}</span>
    </span>
  );
}

function HeaderMobilePlanStatus({
  planExpiry,
  planExpiryStatus,
  screenSummary,
  planName,
  organisationName,
  loadingPlanSubscriptions,
  onExpiredClick,
}) {
  const detailLines = [
    organisationName ? `Organisation: ${organisationName}` : null,
    screenSummary,
    planName ? `Plan: ${planName}` : null,
    planExpiry ? `Expiry: ${planExpiry}` : null,
  ].filter(Boolean);

  const descriptive =
    planExpiryStatus && PLAN_EXPIRY_STATUS_LABEL[planExpiryStatus]
      ? PLAN_EXPIRY_STATUS_LABEL[planExpiryStatus]
      : 'Plan subscription';

  const tooltipTitle = detailLines.length ? detailLines.join('\n') : descriptive;

  if (!planExpiry && loadingPlanSubscriptions)
    return (
      <Tooltip title="Loading plan status" placement="bottom">
        <div
          className="header-mobile-plan-status header-mobile-plan-status--loading"
          aria-busy="true"
          aria-label="Loading plan status"
        >
          <Spin size="small" />
        </div>
      </Tooltip>
    );

  const renewable = planExpiryStatus === 'expired' && typeof onExpiredClick === 'function';
  const statusClass = MOBILE_PLAN_STATUS_CLASS_BY_EXPIRY[planExpiryStatus] ?? 'header-mobile-plan-status--unknown';

  const ariaDetails = detailLines.filter(Boolean).join('. ');
  const ariaLabel = ariaDetails ? `${descriptive}. ${ariaDetails}` : descriptive;

  const beacon = (
    <span className="header-mobile-plan-status__traffic" aria-hidden>
      <span className="header-mobile-plan-status__bulb header-mobile-plan-status__bulb--top" />
      <span className="header-mobile-plan-status__bulb header-mobile-plan-status__bulb--mid" />
      <span className="header-mobile-plan-status__bulb header-mobile-plan-status__bulb--bot" />
    </span>
  );

  const body = renewable ? (
    <button
      type="button"
      className={classNames('header-mobile-plan-status', statusClass)}
      aria-label={`${ariaLabel}. Tap to renew.`}
      onClick={() => onExpiredClick()}
    >
      {beacon}
    </button>
  ) : (
    <div className={classNames('header-mobile-plan-status', statusClass)} role="img" aria-label={ariaLabel}>
      {beacon}
    </div>
  );

  return (
    <Tooltip title={tooltipTitle} placement="bottom">
      {body}
    </Tooltip>
  );
}

const Header = ({
  history,
  location,
  siderWidth,
  hasSider,
  siderVisible,
  purchaseCount,
  toggleSiderVisible,
  headerMainLogo,
  headerMenuLogo,
  isHeaderMenuDualBrandingEnabled,
  currentPlan,
  planExpiry,
  planSubscriptions,
  loadingPlanSubscriptions,
  renewPlan: renewPlanAction,
  fetchPlanSubscriptions: fetchPlanSubscriptionsAction,
  businessInfo,
  organisationList,
  plans,
  brokerInfo,
  selectOrganisation: selectOrganisationAction,
  selectPlan: selectPlanAction,
}) => {
  const [isHeaderScrolledDown, setIsHeaderScrolledDown] = useState(false);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const loggedIn = secureStorage.getItem('token');
  const adminPath = brokerInfo ? '/admin/brokerage-subscriptions' : '/admin';
  const shouldShowMenuBrand = isHeaderMenuDualBrandingEnabled && Boolean(loggedIn) && headerMenuLogo !== 'none';
  const headerLocation = loggedIn ? resolveHeaderLocation(location.pathname) : null;

  // Guard against bootstrap races where unscoped GET /plans briefly fills the store.
  const selectedOrgPlans = useMemo(() => {
    if (!Array.isArray(plans) || plans.length === 0) return [];
    const orgId = businessInfo?.organisationId;
    if (orgId == null) return plans;

    const selectedOrg = (organisationList || []).find((org) => org.organisationId === orgId);
    const branchIds = new Set(
      (selectedOrg?.branches || []).map((branch) => branch?.branchId).filter((id) => id != null),
    );
    if (branchIds.size === 0) return plans;

    return plans.filter((plan) => plan.branchId == null || branchIds.has(plan.branchId));
  }, [plans, businessInfo, organisationList]);

  useEffect(() => {
    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const y = typeof window !== 'undefined' ? window.scrollY || document.documentElement.scrollTop || 0 : 0;
        setIsHeaderScrolledDown(y > HEADER_COMPACT_AFTER_PX);
      });
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!selectedOrgPlans.length) return;
    selectedOrgPlans.forEach((plan) => {
      if (!hasSelectablePlan(plan)) return;
      if (planSubscriptions && Object.prototype.hasOwnProperty.call(planSubscriptions, plan.planId)) return;
      fetchPlanSubscriptionsAction(plan.planId);
    });
  }, [fetchPlanSubscriptionsAction, planSubscriptions, selectedOrgPlans]);

  const navigateHome = () => {
    history.push('/');
  };

  const handleRenewPlan = async (planId, data) => {
    try {
      await renewPlanAction(planId, data);
      message.success('The plan has been successfully renewed');
    } catch (err) {
      const { status } = parseHttpError(err);
      if (status === HttpStatus.PAYMENT_REQUIRED) message.error(MSG_PAYMENT_REQUIRED);
      else message.error(getApiErrorMessage(err, MSG_ERROR));
      throw err;
    }
    try {
      await fetchPlanSubscriptionsAction(planId);
    } catch {
      message.warning('Plan list updated but subscription dates could not be refreshed. Reload the page if needed.');
    }
  };

  const planExpiryStatus = planExpiry ? getPlanExpiryStatus(planExpiry) : null;
  const getPlanStatus = (plan) => {
    if (!hasSelectablePlan(plan)) return 'expired';
    const expiry = getExpiryDate(plan);
    return getPlanExpiryStatus(expiry) || 'expired';
  };
  const openRenewFromHeaderBadge =
    hasSelectablePlan(currentPlan) && planExpiryStatus === 'expired' ? () => setRenewModalOpen(true) : undefined;

  const hasPlanContext = hasSider && hasSelectablePlan(currentPlan);
  const organisationName =
    businessInfo && typeof businessInfo === 'object' && 'name' in businessInfo ? businessInfo.name : '';
  const hasOrgContext = Boolean(hasSider && organisationName);
  const hasEndContext = hasOrgContext || hasPlanContext;
  const showStatusBar = loggedIn && (headerLocation || hasEndContext);

  const handleSelectOrg = async (orgId) => {
    try {
      await selectOrganisationAction(orgId);
      history.push('/organization');
    } catch {
      message.error('Failed to switch organisation');
    }
  };

  const handleSelectPlan = async (plan) => {
    try {
      await selectPlanAction(plan);
      history.push('/dashboard');
    } catch {
      message.error('Failed to switch plan');
    }
  };

  const orgDropdownMenu = (
    <Menu className="header-context-dropdown-menu">
      {Array.isArray(organisationList) && organisationList.length > 0
        ? organisationList.map((org) => (
            <Menu.Item
              key={org.organisationId}
              className={
                businessInfo && org.organisationId === businessInfo.organisationId
                  ? 'header-context-dropdown-menu__item--active'
                  : ''
              }
              aria-selected={Boolean(businessInfo && org.organisationId === businessInfo.organisationId)}
              onClick={() => handleSelectOrg(org.organisationId)}
            >
              {org.name}
            </Menu.Item>
          ))
        : null}
      <Menu.Divider />
      <Menu.Item key="view-all-orgs">
        <Link to="/organization/list">View All Organisations</Link>
      </Menu.Item>
    </Menu>
  );

  const planDropdownMenu = (
    <Menu className="header-context-dropdown-menu">
      {selectedOrgPlans.length > 0
        ? selectedOrgPlans.map((plan) => {
            const optionStatus = getPlanStatus(plan);
            return (
              <Menu.Item
                key={plan.planId}
                className={
                  currentPlan && plan.planId === currentPlan.planId ? 'header-context-dropdown-menu__item--active' : ''
                }
                aria-selected={Boolean(currentPlan && plan.planId === currentPlan.planId)}
                onClick={() => handleSelectPlan(plan)}
              >
                <span className="header-context-dropdown-menu__plan-option">
                  <span className="header-context-dropdown-menu__plan-name" title={plan.name}>
                    {plan.name}
                  </span>
                  <PlanStatusBadge status={optionStatus} compact />
                </span>
              </Menu.Item>
            );
          })
        : null}
      <Menu.Divider />
      <Menu.Item key="view-all-plans">
        <Link to="/dashboard/plans">View All Plans</Link>
      </Menu.Item>
    </Menu>
  );

  return (
    <AntHeader
      className={classNames('header', {
        'header-with-sider': hasSider,
        'header--scrolled': isHeaderScrolledDown,
      })}
    >
      <Link to="/" className="header-brand" style={{ width: siderWidth }} aria-label="ContinuityCoach — home">
        {renderLogo({ logoType: headerMainLogo, className: 'header-main-logo' })}
      </Link>
      {hasSider && (
        <Button
          type="link"
          className="header-hamburger"
          onClick={toggleSiderVisible}
          aria-label={siderVisible ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={!!siderVisible}
          aria-controls="navigation-sider"
        >
          <i className="fa fa-bars" aria-hidden />
        </Button>
      )}
      {shouldShowMenuBrand && (
        <div className="header-menu-brand-logo">
          {renderLogo({ logoType: headerMenuLogo, className: 'header-menu-logo' })}
        </div>
      )}
      {showStatusBar ? (
        <div
          className={classNames('header-status-bar', {
            'header-status-bar--has-end-context': hasEndContext,
          })}
        >
          {headerLocation ? (
            <nav
              className={classNames('header-location header-location--desktop-only', {
                'header-location--multistep': headerLocation.crumbs.length > 2,
              })}
              aria-label="Current screen"
              title={headerLocation.crumbs.some((crumb) => crumb.help) ? undefined : headerLocation.fullTitle}
            >
              <ol className="header-location__list">
                {headerLocation.crumbs.map((crumb, crumbIndex) => {
                  const crumbKey = headerLocation.crumbs
                    .slice(0, crumbIndex + 1)
                    .map((c) => c.label)
                    .join('/');
                  const isLast = crumbIndex === headerLocation.crumbs.length - 1;
                  return (
                    <li key={crumbKey} className="header-location__item">
                      {!isLast && crumb.to ? (
                        <Link to={crumb.to} className="header-location__link">
                          {crumb.label}
                        </Link>
                      ) : (
                        <span
                          className="header-location__current"
                          aria-current={isLast ? 'page' : undefined}
                          title={crumb.label}
                        >
                          {crumb.label}
                        </span>
                      )}
                      {isLast && crumb.help ? <HelpTip title={crumb.help} placement="bottom" /> : null}
                    </li>
                  );
                })}
              </ol>
            </nav>
          ) : null}
          {hasEndContext ? (
            <div className="header-status-end header-status-end--desktop-only">
              {hasOrgContext ? (
                <Dropdown overlay={orgDropdownMenu} trigger={['click']} placement="bottomLeft">
                  <div
                    className="header-plan-context header-plan-context--organisation header-plan-context--clickable"
                    aria-label={`Organisation: ${organisationName}`}
                    aria-haspopup="menu"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => onActivateKeyDown(event, () => event.currentTarget.click())}
                  >
                    <div className="header-plan-context__main">
                      <span className="header-plan-context__label">Organisation:</span>
                      <Icon type="bank" className="header-plan-context__type-icon" aria-hidden />
                      <span className="header-plan-context__name" title={organisationName}>
                        {organisationName}
                      </span>
                      <Icon type="down" className="header-plan-context__caret" aria-hidden />
                    </div>
                  </div>
                </Dropdown>
              ) : null}
              {hasPlanContext ? (
                <Dropdown overlay={planDropdownMenu} trigger={['click']} placement="bottomRight">
                  <div
                    className="header-plan-context header-plan-context--clickable"
                    aria-label={`Plan: ${currentPlan.name}`}
                    aria-haspopup="menu"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => onActivateKeyDown(event, () => event.currentTarget.click())}
                  >
                    <div className="header-plan-context__main">
                      <span className="header-plan-context__label">Plan:</span>
                      <Icon type="file-text" className="header-plan-context__type-icon" aria-hidden />
                      <span className="header-plan-context__name">{currentPlan.name}</span>
                      <Icon type="down" className="header-plan-context__caret" aria-hidden />
                    </div>
                    <div className="header-plan-context__status">
                      {planExpiryStatus ? <PlanStatusBadge status={planExpiryStatus} /> : null}
                      {!planExpiry && loadingPlanSubscriptions ? <Spin size="small" /> : null}
                    </div>
                  </div>
                </Dropdown>
              ) : null}
            </div>
          ) : null}
          {hasEndContext ? (
            <div className="header-status-mobile-end">
              {hasOrgContext ? (
                <Tooltip title={`Organisation: ${organisationName}`} placement="bottom">
                  <Link
                    to="/organization/list"
                    className="header-mobile-org-pill"
                    aria-label={`Organisation: ${organisationName}`}
                  >
                    <Icon type="home" aria-hidden />
                  </Link>
                </Tooltip>
              ) : null}
              {hasPlanContext ? (
                <HeaderMobilePlanStatus
                  planExpiry={planExpiry}
                  planExpiryStatus={planExpiryStatus}
                  screenSummary={headerLocation?.fullTitle}
                  planName={currentPlan.name}
                  organisationName={hasOrgContext ? organisationName : undefined}
                  loadingPlanSubscriptions={loadingPlanSubscriptions}
                  onExpiredClick={openRenewFromHeaderBadge}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {loggedIn ? (
        <>
          <Menu
            theme="light"
            mode="horizontal"
            selectedKeys={[`/${location.pathname.split('/')[1]}`]}
            className="header-menu"
          >
            <Menu.Item key="/dashboard" className="header-menu-item">
              <Link to="/dashboard">
                <Icon type="dashboard" aria-hidden />
                <span className="header-nav-label">Dashboard</span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/admin" className="header-menu-item">
              <Link to={adminPath}>
                <Badge dot={!!purchaseCount} offset={[24, 0]}>
                  <Icon type="setting" aria-hidden />
                </Badge>
                <span className="header-nav-label">Admin</span>
              </Link>
            </Menu.Item>
          </Menu>
          <AntdModal
            title="Renew plan subscription"
            visible={renewModalOpen}
            onCancel={() => setRenewModalOpen(false)}
            footer={null}
            width="min(600px, 92vw)"
            destroyOnClose
            keyboard={false}
            className="modal"
          >
            {hasSelectablePlan(currentPlan) ? (
              <PlanEdit
                plan={currentPlan}
                action={PLAN_ACTIONS.RENEW}
                renewPlan={handleRenewPlan}
                close={() => setRenewModalOpen(false)}
              />
            ) : null}
          </AntdModal>
        </>
      ) : (
        // <Modal
        //   toggle={show => (
        //     <Button icon="login" onClick={show} className="header-login-btn">
        //       Register / Log in
        //     </Button>
        //   )}
        // >
        <Modal
          toggle={(show) => (
            <Button
              type="link"
              icon="login"
              onClick={() => {
                show();
                navigateHome();
              }}
              className="header-login-btn"
              data-testid="header-sign-in"
            >
              Sign in
            </Button>
          )}
        >
          {(hide) => <Login close={hide} />}
        </Modal>
      )}
    </AntHeader>
  );
};

const mapStateToProps = (state) => ({
  purchaseCount: state.purchaseReducer.purchaseCount,
  headerMainLogo: headerMainLogoSelector(state),
  headerMenuLogo: headerMenuLogoSelector(state),
  isHeaderMenuDualBrandingEnabled: headerMenuDualBrandingSelector(state),
  businessInfo: businessInfoSelector(state),
  currentPlan: planSelector(state),
  planExpiry: planExpiryDateSelector(state),
  planSubscriptions: planSubscriptionsSelector(state),
  loadingPlanSubscriptions: loadingPlanSubscriptionsSelector(state),
  organisationList: Array.isArray(state.organisationReducer.organisationList)
    ? state.organisationReducer.organisationList.filter((o) => !o.isArchived)
    : [],
  plans: plansSelector(state) || [],
  brokerInfo: brokerInfoSelector(state),
});

const mapDispatchToProps = {
  renewPlan,
  fetchPlanSubscriptions,
  selectOrganisation,
  selectPlan,
};

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(Header);
